import * as React from "react";
import { useToggle } from "react-use";
import { toast } from "react-toastify";
import { BusinessReportOutcomeResult } from "@/modules/snapshots/businessReports.types";
import {
  useDeleteBusinessReportOutcomeNoteMutation,
  useUpdateBusinessReportOutcomeResultMutation,
} from "@/modules/snapshots/businessReports.api";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2 } from "lucide-react";
import { handleNoteError } from "@/modules/snapshots/components/noteErrors";

interface Props {
  note: BusinessReportOutcomeResult;
  outcomeId: string;
  compact?: boolean;
}

export const NoteItem: React.FC<Props> = ({ note, outcomeId, compact = false }) => {
  const [isEditing, toggleIsEditing] = useToggle(false);
  const [editValue, setEditValue] = React.useState(note.result);
  const [updateResult, { isLoading: isUpdating }] = useUpdateBusinessReportOutcomeResultMutation();
  const [deleteNote, { isLoading: isDeleting }] = useDeleteBusinessReportOutcomeNoteMutation();

  React.useEffect(() => {
    setEditValue(note.result);
  }, [note.result]);

  const handleSave = async () => {
    const trimmed = editValue.trim();
    if (!trimmed || trimmed === note.result) {
      toggleIsEditing(false);
      return;
    }

    try {
      await updateResult({
        id: outcomeId,
        resultId: note.id,
        data: { result: trimmed },
      }).unwrap();
      toast.success("Note saved");
      toggleIsEditing(false);
    } catch (error) {
      handleNoteError(error, "Failed to save note");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this note?")) return;

    try {
      await deleteNote({ outcomeId, resultId: note.id }).unwrap();
      toast.success("Note deleted");
    } catch (error) {
      handleNoteError(error, "Failed to delete note");
    }
  };

  const styles = compact
    ? {
        padding: "p-2",
        minHeight: "min-h-[60px]",
        borderColor: "border-gray-100",
        iconSize: "h-3 w-3",
        hoverBg: "hover:bg-gray-100",
      }
    : {
        padding: "p-3",
        minHeight: "min-h-[80px]",
        borderColor: "border-gray-200",
        iconSize: "h-3.5 w-3.5",
        hoverBg: "hover:bg-gray-200",
      };

  if (isEditing) {
    return (
      <div className={`rounded border border-gray-200 bg-white ${styles.padding}`}>
        <Textarea
          value={editValue}
          onChange={e => setEditValue(e.target.value)}
          className={`${styles.minHeight} w-full bg-white text-sm`}
        />
        <div className="mt-2 flex justify-end gap-2">
          <Button
            variant="second-white"
            size="sm"
            onClick={() => {
              setEditValue(note.result);
              toggleIsEditing(false);
            }}
            disabled={isUpdating}
          >
            Cancel
          </Button>
          <Button variant="default" size="sm" onClick={handleSave} disabled={isUpdating}>
            {isUpdating ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`group/note flex items-start gap-2 rounded border ${styles.borderColor} bg-white ${styles.padding}`}
    >
      <p className="min-w-0 flex-1 whitespace-pre-wrap text-sm text-gray-600">{note.result}</p>
      <div className="flex shrink-0 gap-1 opacity-0 group-hover/note:opacity-100">
        <button
          onClick={() => toggleIsEditing(true)}
          className={`rounded p-1 text-gray-400 ${styles.hoverBg} hover:text-gray-600`}
        >
          <Pencil className={styles.iconSize} />
        </button>
        <button
          onClick={handleDelete}
          disabled={isDeleting}
          className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500"
        >
          <Trash2 className={styles.iconSize} />
        </button>
      </div>
    </div>
  );
};
